import { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { fmtIDR, fmtDate } from "../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";

export function ReceiptModal({ trxId, open, onClose }) {
  const { t } = useLang();
  const [trx, setTrx] = useState(null);

  useEffect(() => {
    if (open && trxId) {
      api.get(`/pos/transactions/${trxId}`).then((r) => setTrx(r.data)).catch(() => {});
    }
  }, [open, trxId]);

  const body = trx && (
    <div className="font-mono text-xs leading-relaxed text-black">
      <div className="text-center mb-3">
        <div className="font-bold text-sm">{trx.settings?.receipt_header || trx.settings?.business_name}</div>
        {trx.outlet?.name && <div>{trx.outlet.name}</div>}
        {trx.outlet?.address && <div>{trx.outlet.address}</div>}
        <div>{fmtDate(trx.created_at)}</div>
        <div>{trx.trx_number} · {trx.cashier_name}</div>
      </div>
      <div className="border-t border-dashed border-black my-2" />
      {(trx.items || []).map((i, idx) => (
        <div key={i.product_id || `${i.name}-${idx}`} className="flex justify-between gap-2">
          <span>{i.name} x{i.qty}</span>
          <span>{fmtIDR(i.subtotal)}</span>
        </div>
      ))}
      <div className="border-t border-dashed border-black my-2" />
      <div className="flex justify-between"><span>{t("subtotal")}</span><span>{fmtIDR(trx.subtotal)}</span></div>
      {trx.discount > 0 && <div className="flex justify-between"><span>{t("discount")}</span><span>-{fmtIDR(trx.discount)}</span></div>}
      {trx.service_amount > 0 && <div className="flex justify-between"><span>{t("service")} {(trx.service_pct * 100).toFixed(0)}%</span><span>{fmtIDR(trx.service_amount)}</span></div>}
      {trx.tax_amount > 0 && <div className="flex justify-between"><span>{t("tax")} {(trx.tax_pct * 100).toFixed(0)}%</span><span>{fmtIDR(trx.tax_amount)}</span></div>}
      <div className="flex justify-between font-bold text-sm border-t border-dashed border-black mt-2 pt-2"><span>{t("total")}</span><span>{fmtIDR(trx.total)}</span></div>
      <div className="flex justify-between"><span>{trx.payment_method?.toUpperCase()}</span><span>{fmtIDR(trx.paid_amount)}</span></div>
      <div className="flex justify-between"><span>{t("change")}</span><span>{fmtIDR(trx.change)}</span></div>
      <div className="border-t border-dashed border-black my-2" />
      <div className="text-center">{trx.settings?.receipt_footer}</div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm" data-testid="receipt-modal">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("printReceipt")}</DialogTitle>
        </DialogHeader>
        <div className="bg-white rounded-lg p-4 border border-border max-h-[50vh] overflow-y-auto">{body}</div>
        <Button data-testid="print-receipt-btn" onClick={() => window.print()} className="bg-amber-600 hover:bg-amber-700 text-white">
          <Printer className="w-4 h-4 mr-2" /> {t("printReceipt")}
        </Button>
        <div className="receipt-print">{body}</div>
      </DialogContent>
    </Dialog>
  );
}
